import { ConfigEntryRefFuncName, ConfigEntryType } from 'config/config';
import { filter, orderBy, uniqBy } from 'lodash';
import { ConfigResponseType } from '../apis/ConfigAPI';
import { configKeys, useConfigEntryOptionsQuery } from '../queries';
import { JAVA_SERVICE_ENDPOINTS, JavaAPI } from '@buildwise/libs/services';
import { ConfigAPI } from '../apis';
import { INSIGHTS_DATE_OPTIONS, INSIGHTS_TYPE_OPTIONS } from '../constants';
import { ConfigType } from 'config/config';
import { difference } from 'lodash';
import { toast } from 'react-toastify';
import { catchErrorMessage } from 'utility/helpers';

export const getConfigQueryOptions = ({
  shouldFetch = true,
  ...params
}: {
  companyId?: string;
  configName?: string;
  configType?: string;
  configCategory?: string;
  shouldFetch?: boolean;
  productLevel?: boolean;
}) => {
  return {
    queryKey: configKeys.getConfig(params),
    staleTime: 5 * 60 * 1000,
    keepPreviousData: true,
    enabled: shouldFetch,
    queryFn: () => ConfigAPI.getConfig(params),
  };
};

export const getConfigEntryQueryOptions = ({
  companyId,
  configEntry,
  shouldFetch = true,
}: {
  companyId?: string;
  configEntry: ConfigEntryType;
  shouldFetch: boolean;
}) => {
  const endpoint = configEntry.targetMapper;
  const configName = configEntry.referenceObject?.configRefName || configEntry.entryCode;
  const JavaServiceAPI = new JavaAPI();

  return {
    queryKey: configKeys.getConfig({
      companyId,
      configName,
      targetMapper: endpoint,
    }),
    staleTime: 5 * 60 * 1000,
    keepPreviousData: true,
    enabled:
      shouldFetch && !!(endpoint || configName) && !configEntry.referenceObject?.functionName,
    queryFn: () =>
      configEntry.targetMapperType === 'API' && endpoint
        ? JavaServiceAPI.get(
            endpoint
              ?.replace(JAVA_SERVICE_ENDPOINTS.API_VERSION_PREFIX, '')
              ?.replace(':companyId', companyId || ''),
          )
        : ConfigAPI.getConfig({
            companyId,
            configName,
          }),
  };
};

export const getConfigEntries = ({ data }: { data: ConfigResponseType | undefined }) =>
  uniqBy<ConfigEntryType>(
    filter<ConfigEntryType>(data?.config?.entries || [], (entry) => entry.isActive),
    (entry) =>
      typeof entry.entryCode === 'string' ? entry.entryCode.toLowerCase() : entry.entryCode,
  );

export const getConfigEntriesList = ({ data }: { data: ConfigEntryType[] }) =>
  data.map((entry) => entry.entryCode);

export const getConfigEntryOptionsByFuncName = ({ name }: { name: ConfigEntryRefFuncName }) => {
  switch (name) {
    case ConfigEntryRefFuncName.INSIGHTS_DATE: {
      return INSIGHTS_DATE_OPTIONS;
    }

    case ConfigEntryRefFuncName.INSIGHTS_TYPES: {
      return INSIGHTS_TYPE_OPTIONS;
    }

    default:
      return [];
  }
};

export const getConfigEntryOptions = ({
  configEntry,
  defaultOptions = [],
  data,
}: {
  configEntry: ConfigEntryType;
  defaultOptions?: { label: string; value: string; groupEntryBy?: string }[];
  data: ConfigResponseType | Record<string, any>[] | undefined;
}): { label: string; value: string; groupEntryBy?: string }[] => {
  if (configEntry.referenceObject?.functionName) {
    return getConfigEntryOptionsByFuncName({ name: configEntry.referenceObject?.functionName });
  }

  return orderBy(
    uniqBy(
      filter(
        [
          ...((configEntry.targetMapperType === 'API' && configEntry.targetMapper
            ? (data as Record<string, string>[])?.map((item) => ({
                label: item?.[configEntry?.targetMapperLabelName || ''],
                value: item?.[configEntry?.targetMapperValueKey || ''],
              })) || []
            : (data as ConfigResponseType)?.config?.entries
                ?.filter((entry) => entry.isActive)
                ?.map((entry) => ({
                  label: entry.referenceObject?.label || entry.entryDescription || entry.entryCode,
                  value: entry.entryCode,
                  groupEntryBy: entry?.referenceObject?.groupEntryBy,
                }))) || []),
          ...defaultOptions,
        ],
        (entry) => !!entry.value,
      ),
      'value',
    ),
  );
};

export const updateConfig = async ({
  newValue,
  config,
  companyId,
  updatedEntries,
}: {
  newValue: string | string[];
  config: ConfigType;
  companyId?: string;
  updatedEntries?: ConfigEntryType[];
}) => {
  try {
    let entriesToAdd: ConfigEntryType[] = updatedEntries || [];
    if (!updatedEntries?.length && newValue) {
      const prevEntries = config?.entries?.map((config) => config?.entryCode);
      const newValues = typeof newValue === 'string' ? [newValue] : newValue;
      const newEntries = difference(newValues, prevEntries);

      if (newEntries.length > 0) {
        entriesToAdd = newEntries.map((topic) => ({
          entryCode: topic,
          entryDescription: topic,
          isActive: true,
          entryValues: [],
        }));
      }
    }

    if (entriesToAdd?.length > 0) {
      const { entries } = config;
      const updatedEntriesList: ConfigEntryType[] = [...entries, ...entriesToAdd];
      const updatedConfig = { ...config, entries: updatedEntriesList };

      // If it's a product config, create at the company level
      if (!updatedConfig.companyId && companyId) {
        updatedConfig['companyId'] = companyId;
        delete (updatedConfig as any).id;
      }

      const data = await ConfigAPI.updateConfig(updatedConfig);
      return data;
    }
  } catch (e) {
    toast.error(catchErrorMessage(e));
  }
};
